/**
 * Pins a specific product to the first slot of the collection grid when a
 * `first` URL param is present (Facebook catalog / ad landing links).
 * Optional `variant` param carries the ad's variant through to the product page.
 *
 * Usage: /collections/best-sellers?first=product-handle&variant=1234567890
 * Also accepts a full/encoded product URL for `first` (Facebook catalog deep links).
 *
 * Fast path: if the target product's card is already server-rendered on the
 * page, just move it to slot 1 (works for anything in the first ~24 items).
 * Fallback: for products ranked far down a large collection (never in that
 * initial render), fetch the product directly via Shopify's standard
 * /products/{handle}.js AJAX endpoint and build a minimal card from it.
 */
(() => {
  const params = new URLSearchParams(window.location.search);
  const rawFirst = params.get('first');
  if (!rawFirst) return;

  let targetHandle = rawFirst;
  let variantId = params.get('variant');

  try {
    const asUrl = new URL(rawFirst, window.location.origin);
    const match = asUrl.pathname.match(/\/products\/([^/?#]+)/);
    if (match) {
      targetHandle = match[1];
      if (!variantId) variantId = asUrl.searchParams.get('variant');
    }
  } catch {
    // rawFirst wasn't parseable as a URL — treat it as a bare handle.
  }

  const pinRendered = (grid) => {
    const cards = grid.querySelectorAll(':scope > li.product-grid__item');
    if (!cards.length) return false;

    let targetLi = null;
    for (const li of cards) {
      const link = li.querySelector('a.product-card__link[href]');
      const handle = link
        ? new URL(link.getAttribute('href'), window.location.origin).pathname
            .split('/products/')[1]
            ?.split(/[/?#]/)[0]
        : null;
      const idMatch = /^\d+$/.test(rawFirst) && li.querySelector(`product-card[data-product-id="${rawFirst}"]`);
      if (handle === targetHandle || idMatch) {
        targetLi = li;
        break;
      }
    }
    if (!targetLi) return false;

    if (cards[0] !== targetLi) {
      grid.insertBefore(targetLi, cards[0]);
    }
    targetLi.setAttribute('data-pinned-first', 'true');

    if (variantId) {
      const productCard = targetLi.querySelector('product-card');
      if (productCard && typeof productCard.applyVariantToLinks === 'function') {
        productCard.applyVariantToLinks(variantId);
      }
    }
    return true;
  };

  const buildFallbackCard = (product) => {
    const variant = variantId
      ? product.variants.find((v) => String(v.id) === String(variantId))
      : product.variants[0];
    const image =
      (variant && variant.featured_image && variant.featured_image.src) ||
      product.featured_image ||
      (product.images && product.images[0]);
    const price = ((variant ? variant.price : product.price) / 100).toFixed(2);
    const url = variantId ? `${product.url}?variant=${variantId}` : product.url;

    const li = document.createElement('li');
    li.className = 'product-grid__item';
    li.setAttribute('data-pinned-first', 'true');
    li.setAttribute('data-pinned-fallback', 'true');

    const a = document.createElement('a');
    a.href = url;
    a.style.cssText = 'display:block;text-decoration:none;color:inherit;';

    if (image) {
      const img = document.createElement('img');
      img.src = image.includes('?') ? `${image}&width=600` : `${image}?width=600`;
      img.alt = product.title;
      img.loading = 'eager';
      img.style.cssText = 'width:100%;height:auto;display:block;aspect-ratio:1;object-fit:cover;';
      a.appendChild(img);
    }

    const info = document.createElement('div');
    info.style.cssText = 'padding-block:0.5rem;';
    const h3 = document.createElement('h3');
    h3.style.cssText = 'font-size:1rem;margin:0 0 0.25rem;';
    h3.textContent = product.title;
    const p = document.createElement('p');
    p.style.margin = '0';
    p.textContent = `$${price}`;
    info.appendChild(h3);
    info.appendChild(p);
    a.appendChild(info);
    li.appendChild(a);
    return li;
  };

  const pinFallback = async (grid) => {
    let product;
    try {
      const res = await fetch(`/products/${targetHandle}.js`);
      if (!res.ok) return;
      product = await res.json();
    } catch {
      return;
    }
    const li = buildFallbackCard(product);
    const firstCard = grid.querySelector(':scope > li.product-grid__item');
    if (firstCard) grid.insertBefore(li, firstCard);
    else grid.prepend(li);
  };

  const pinProduct = async () => {
    const grid = document.querySelector('.product-grid[data-testid="product-grid"]');
    if (!grid) return;
    const found = pinRendered(grid);
    if (!found) await pinFallback(grid);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', pinProduct, { once: true });
  } else {
    pinProduct();
  }
})();
